import { setMenu } from './menu';
import { lenis } from './scroll';

/*
  Tiroir de contact (partials/contact-drawer.twig) :
  [data-contact-open] ouvre, [data-contact-close] / [data-contact-overlay] ferment,
  [data-contact-drawer] (.is-open) contient le formulaire HubSpot.
  Panneau ouvert, le reste de la page est inerte et le focus clavier y reste piégé.

  Le script HubSpot ([data-hubspot-src]) n'est chargé qu'au premier survol, focus
  ou clic d'un bouton d'ouverture : il remplit alors le bloc .hs-form-frame.
*/

const drawerEl = () => document.querySelector<HTMLElement>('[data-contact-drawer]');
const overlayEl = () => document.querySelector<HTMLElement>('[data-contact-overlay]');
let opener: HTMLElement | null = null;
/** Enfants du body rendus inertes à l'ouverture, seuls à être rétablis à la fermeture. */
let inertedSiblings: Element[] = [];

/** Rend inerte le reste de la page pendant que le panneau (modal) est ouvert. */
function setBackgroundInert(inert: boolean, drawer: HTMLElement, overlay: HTMLElement) {
  if (!inert) {
    for (const element of inertedSiblings) element.removeAttribute('inert');
    inertedSiblings = [];
    return;
  }
  for (const element of document.body.children) {
    if (element === drawer || element === overlay || element.tagName === 'SCRIPT') continue;
    // déjà inerte (menu fermé…) : on le laisse tel quel à la fermeture
    if (element.hasAttribute('inert')) continue;
    element.setAttribute('inert', '');
    inertedSiblings.push(element);
  }
}

let hubspotRequested = false;

function loadHubSpot() {
  const src = drawerEl()?.dataset.hubspotSrc;
  if (hubspotRequested || !src) return;
  hubspotRequested = true;
  const script = document.createElement('script');
  script.src = src;
  script.defer = true;
  script.addEventListener('error', () => {
    // nouvelle tentative à la prochaine ouverture
    hubspotRequested = false;
    script.remove();
  });
  document.head.append(script);
}

export function setDrawer(open: boolean, { restoreFocus = true } = {}) {
  const drawer = drawerEl();
  const overlay = overlayEl();
  if (!drawer || !overlay || drawer.classList.contains('is-open') === open) return;

  if (open) {
    loadHubSpot();
    overlay.hidden = false;
    void overlay.offsetWidth; // le fondu part bien de 0
  }
  drawer.classList.toggle('is-open', open);
  overlay.classList.toggle('is-open', open);
  drawer.toggleAttribute('inert', !open);
  drawer.setAttribute('aria-hidden', String(!open));
  setBackgroundInert(open, drawer, overlay);
  for (const button of document.querySelectorAll('[data-contact-open]')) {
    button.setAttribute('aria-expanded', String(open));
  }

  if (open) {
    lenis?.stop();
    drawer.scrollTop = 0;
    drawer.querySelector<HTMLElement>('[data-contact-close]')?.focus({ preventScroll: true });
  } else {
    lenis?.start();
    window.setTimeout(() => {
      if (drawer.classList.contains('is-open')) return;
      overlay.hidden = true;
    }, 650);
    if (restoreFocus) opener?.focus({ preventScroll: true });
  }
}

function trapFocus(event: KeyboardEvent, drawer: HTMLElement) {
  const focusable = [...drawer.querySelectorAll<HTMLElement>('button, a[href], iframe')].filter(
    (el) => !el.hidden && el.offsetParent !== null,
  );
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export function initContactDrawer() {
  document.addEventListener('click', (event) => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    const openButton = target.closest<HTMLElement>('[data-contact-open]');
    if (openButton) {
      opener = openButton;
      setMenu(false, { restoreFocus: false });
      setDrawer(true);
    } else if (target.closest('[data-contact-close]') || target.closest('[data-contact-overlay]')) {
      setDrawer(false);
    }
  });

  document.addEventListener('keydown', (event) => {
    const drawer = drawerEl();
    if (!drawer?.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      setDrawer(false);
      return;
    }
    // garder le focus dans le panneau ouvert
    if (event.key === 'Tab') trapFocus(event, drawer);
  });

  // préchargement du formulaire dès qu'un bouton d'ouverture est survolé ou atteint au clavier
  for (const type of ['pointerover', 'focusin'] as const) {
    document.addEventListener(type, (event) => {
      if ((event.target as Element | null)?.closest?.('[data-contact-open]')) loadHubSpot();
    });
  }
}
